import React, {useEffect, useState} from 'react'
import {useLocation, useSearchParams} from 'react-router-dom'
import {LocalLink} from 'i18n/LocalLink'
import styled from 'styled-components'
import {Helmet} from 'react-helmet'
import Page from 'attendee/theme/Page'
import {FullPageLoader} from 'attendee/theme/FullPageLoader'
import {routes} from 'routes/routes'
import {DEFAULT_OG_IMAGE, SITE_ORIGIN} from 'seo/siteMeta'
import {useI18n} from 'i18n/I18nContext'
import {appLocaleToBcp47, type AppLocale} from 'i18n/locales'
import {blogApiClient, BlogPost, BlogPostsResponse} from 'blog/apiClient'
import {publicImageUrl} from 'organization/ImageSelector'
import defaultEventImage from 'images/defaultEventImage.webp'

function parseBlogPage(raw: string | null): number {
  const n = parseInt(raw ?? '1', 10)
  return Number.isFinite(n) && n >= 1 ? n : 1
}

/** Page numbers to render (with null = ellipsis gap). */
function paginationWindow(current: number, last: number): (number | null)[] {
  if (last <= 1) return []
  if (last <= 9) {
    return Array.from({length: last}, (_, i) => i + 1)
  }
  const pages: (number | null)[] = []
  const pushRange = (from: number, to: number) => {
    for (let p = from; p <= to; p++) pages.push(p)
  }
  pages.push(1)
  const left = Math.max(2, current - 1)
  const right = Math.min(last - 1, current + 1)
  if (left > 2) pages.push(null)
  pushRange(left, right)
  if (right < last - 1) pages.push(null)
  pages.push(last)
  return pages
}

export function BlogIndex() {
  const {t, locale} = useI18n()
  const {pathname, search: locationSearch} = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()

  const search = searchParams.get('search') ?? ''
  const category = searchParams.get('category') ?? ''
  const tag = searchParams.get('tag') ?? ''
  const page = parseBlogPage(searchParams.get('page'))

  const [data, setData] = useState<BlogPostsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [searchInput, setSearchInput] = useState(search)

  useEffect(() => {
    setLoading(true)
    blogApiClient
      .getPosts({
        search: search || undefined,
        category: category || undefined,
        tag: tag || undefined,
        page,
      })
      .then(setData)
      .finally(() => setLoading(false))
  }, [search, category, tag, page])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (searchInput) next.set('search', searchInput)
      else next.delete('search')
      next.delete('page')
      return next
    })
  }

  const setFilter = (key: 'category' | 'tag', value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (value) next.set(key, value)
      else next.delete(key)
      next.delete('page')
      return next
    })
  }

  const clearFilters = () => {
    setSearchInput('')
    setSearchParams({})
  }

  const goToPage = (p: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('page', String(p))
      return next
    })
    window.scrollTo({top: 0, behavior: 'smooth'})
  }

  if (loading && !data) return <FullPageLoader />

  const posts = data?.posts.data ?? []
  const lastPage = data?.posts.last_page ?? 1
  const currentPage = data?.posts.current_page ?? 1
  const pageNumbers = data ? paginationWindow(currentPage, lastPage) : []

  return (
    <Page>
      <>
        <Helmet>
          <title>{t('blog.indexMetaTitle')}</title>
          <meta name="description" content={t('blog.indexMetaDescription')} />
          <link
            rel="canonical"
            href={`${SITE_ORIGIN}${pathname}${locationSearch}`}
          />
          <meta property="og:title" content={t('blog.ogTitle')} />
          <meta property="og:description" content={t('blog.ogDescription')} />
          <meta
            property="og:url"
            content={`${SITE_ORIGIN}${pathname}${locationSearch}`}
          />
          <meta property="og:image" content={DEFAULT_OG_IMAGE} />
          <meta property="og:image:secure_url" content={DEFAULT_OG_IMAGE} />
          <meta property="og:image:type" content="image/jpeg" />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={t('blog.ogTitle')} />
          <meta
            name="twitter:description"
            content={t('blog.ogDescription')}
          />
          <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
        </Helmet>

        {/* Hero */}
        <section className="contact_section dark_light_class pt-55 pb-40">
          <div className="container">
            <div className="section__title-wrapper mb-40">
              <span className="section__back-title">{t('blog.heroBack')}</span>
              <span className="section__subtitle">
                {t('blog.heroSubtitle')}
              </span>
              <h1 className="section__title">
                <span className="text__highlight">
                  {t('blog.heroTitleHighlight')}
                </span>
              </h1>
              <p style={{marginTop: 8}}>{t('blog.heroLead')}</p>
            </div>

            {/* Search */}
            <form onSubmit={handleSearch} className="mb-20">
              <div style={{display: 'flex', gap: 8, flexWrap: 'wrap'}}>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t('blog.searchPlaceholder')}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  style={{flex: 1, minWidth: 200}}
                />
                <button type="submit" className="input__btn">
                  {t('blog.searchButton')}
                </button>
                {(search || category || tag) && (
                  <button
                    type="button"
                    className="input__btn"
                    style={{background: '#f5f5f5', color: '#555'}}
                    onClick={clearFilters}
                  >
                    ✕ {t('blog.clearFilters')}
                  </button>
                )}
              </div>
            </form>

            {/* Category chips */}
            {data && data.categories.length > 0 && (
              <FilterRow className="mb-10">
                {data.categories.map((cat) => (
                  <FilterChip
                    key={cat.slug}
                    $active={category === cat.slug}
                    $color={cat.color}
                    onClick={() =>
                      setFilter(
                        'category',
                        category === cat.slug ? '' : cat.slug,
                      )
                    }
                  >
                    {cat.name}
                  </FilterChip>
                ))}
              </FilterRow>
            )}

            {/* Tag chips */}
            {data && data.tags.length > 0 && (
              <FilterRow className="mb-30">
                {data.tags.map((t) => (
                  <FilterChip
                    key={t.slug}
                    $active={tag === t.slug}
                    onClick={() =>
                      setFilter('tag', tag === t.slug ? '' : t.slug)
                    }
                  >
                    #{t.name}
                  </FilterChip>
                ))}
              </FilterRow>
            )}
          </div>
        </section>

        {/* Posts */}
        <section className="event__schedule-area dark_light_class pb-80">
          <div className="container">
            {loading && (
              <p style={{textAlign: 'center', padding: 40, color: '#888'}}>
                {t('blog.loading')}
              </p>
            )}

            {!loading && posts.length === 0 && (
              <div style={{textAlign: 'center', padding: '60px 20px'}}>
                <h4>{t('blog.emptyTitle')}</h4>
                <button
                  type="button"
                  className="input__btn mt-20"
                  onClick={clearFilters}
                >
                  {t('blog.emptyCta')}
                </button>
              </div>
            )}

            <div className="row">
              {posts.map((post) => (
                <PostCard key={post.id} post={post} locale={locale} t={t} />
              ))}
            </div>

            {/* Pagination */}
            {data && lastPage > 1 && (
              <PaginationWrap>
                <PaginationMeta>
                  {t('blog.pageXofY')
                    .replace('{current}', String(currentPage))
                    .replace('{last}', String(lastPage))}
                  {data.posts.total != null
                    ? t('blog.totalArticlesSuffix').replace(
                        '{n}',
                        String(data.posts.total),
                      )
                    : ''}
                </PaginationMeta>
                <PaginationRow>
                  <PageBtn
                    type="button"
                    $ghost
                    disabled={currentPage <= 1}
                    onClick={() => goToPage(currentPage - 1)}
                    aria-label={t('blog.prevPageAria')}
                  >
                    ←
                  </PageBtn>
                  {pageNumbers.map((p, idx) =>
                    p == null ? (
                      <PaginationEllipsis key={`e-${idx}`}>
                        …
                      </PaginationEllipsis>
                    ) : (
                      <PageBtn
                        key={p}
                        type="button"
                        $active={p === currentPage}
                        onClick={() => goToPage(p)}
                      >
                        {p}
                      </PageBtn>
                    ),
                  )}
                  <PageBtn
                    type="button"
                    $ghost
                    disabled={currentPage >= lastPage}
                    onClick={() => goToPage(currentPage + 1)}
                    aria-label={t('blog.nextPageAria')}
                  >
                    →
                  </PageBtn>
                </PaginationRow>
              </PaginationWrap>
            )}
          </div>
        </section>
      </>
    </Page>
  )
}

function PostCard({
  post,
  locale,
  t,
}: {
  post: BlogPost
  locale: AppLocale
  t: (k: string) => string
}) {
  const image = post.featured_image
    ? publicImageUrl(post.featured_image)
    : defaultEventImage
  const formattedDate = post.published_at
    ? new Date(post.published_at).toLocaleDateString(appLocaleToBcp47(locale), {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null

  return (
    <div className="col-xxl-4 col-xl-4 col-lg-6 col-md-6 col-xs-12">
      <CardHolder
        className="event__venue-card"
        style={{borderBottom: '2px solid #f7426f', position: 'relative'}}
      >
        <LocalLink to={`${routes.blog.root}/${post.slug}`}>
          <CardImage $background={image as string} />
        </LocalLink>

        {post.category && (
          <div
            className="event__tag-post"
            style={{top: 20, left: 20, bottom: 'auto'}}
          >
            <div className="event__tag-item" style={{fontSize: 11}}>
              <span style={{background: post.category.color}}>
                {post.category.name}
              </span>
            </div>
          </div>
        )}

        <CardBody>
          <CardTitle>
            <LocalLink to={`${routes.blog.root}/${post.slug}`}>
              {post.title}
            </LocalLink>
          </CardTitle>

          {post.excerpt && <CardExcerpt>{post.excerpt}</CardExcerpt>}

          <div className="event__venue-meta">
            <div
              className="news__meta-status"
              style={{flexWrap: 'wrap', gap: '8px 16px'}}
            >
              {post.author && (
                <span>
                  <i className="flaticon-user mr-5"></i>
                  {post.author.name}
                </span>
              )}
              {formattedDate && (
                <span>
                  <i className="flaticon-calendar-1 mr-5"></i>
                  {formattedDate}
                </span>
              )}
              <span style={{color: '#f7426f', fontWeight: 600}}>
                ⏱{' '}
                {t('blog.minReadShort').replace(
                  '{n}',
                  String(post.reading_time),
                )}
              </span>
            </div>
          </div>

          <LocalLink
            to={`${routes.blog.root}/${post.slug}`}
            className="input__btn w-100 mt-20"
          >
            {t('blog.readMore')}
          </LocalLink>
        </CardBody>
      </CardHolder>
    </div>
  )
}

/* ─── Styled ─────────────────────────────────────────────────────────────── */

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const FilterChip = styled.button<{$active?: boolean; $color?: string}>`
  padding: 4px 14px;
  border-radius: 20px;
  border: 1.5px solid
    ${(p) => (p.$active ? (p.$color ?? 'var(--clr-gradient-14)') : '#ddd')};
  background: ${(p) =>
    p.$active ? (p.$color ?? 'var(--clr-gradient-14)') : 'transparent'};
  color: ${(p) => (p.$active ? '#fff' : '#555')};
  font-size: 0.82rem;
  cursor: pointer;
  transition: all 0.15s;
`

const CardHolder = styled.div`
  padding: 0;
  margin: 30px 0;
  background: #f5f5f5;
`

const CardImage = styled.div<{$background: string}>`
  width: 100%;
  height: 200px;
  background-image: url(${(p) => p.$background});
  background-size: cover;
  background-position: center;
  display: block;
`

const CardBody = styled.div`
  padding: 15px;
`

const CardTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  margin: 0 0 8px;
  line-height: 1.4;
  & a {
    color: inherit;
    text-decoration: none;
    &:hover {
      color: var(--clr-theme-1);
    }
  }
`

const CardExcerpt = styled.p`
  font-size: 0.875rem;
  color: #666;
  margin: 0 0 10px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const PaginationWrap = styled.div`
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`

const PaginationMeta = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: #666;
`

const PaginationRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const PaginationEllipsis = styled.span`
  min-width: 38px;
  text-align: center;
  color: #888;
  user-select: none;
`

const PageBtn = styled.button<{$active?: boolean; $ghost?: boolean}>`
  min-width: 38px;
  height: 38px;
  padding: 0 10px;
  border-radius: 6px;
  border: 1.5px solid
    ${(p) => (p.$active ? '#f7426f' : p.$ghost ? '#ddd' : '#ddd')};
  background: ${(p) =>
    p.$active ? '#f7426f' : p.$ghost ? '#fafafa' : 'transparent'};
  color: ${(p) => (p.$active ? '#fff' : '#555')};
  font-weight: ${(p) => (p.$active ? '700' : '400')};
  cursor: pointer;
  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`
